import React, { useState, useCallback, useEffect } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  InlineError,
  Toast,
  Button,
  Select,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { DeleteIcon } from "@shopify/polaris-icons";

const CURRENCY_OPTIONS = [
  { label: "QAR", value: "QAR" },
  { label: "USD", value: "USD" },
  { label: "EUR", value: "EUR" },
];

const Currency = () => {
  const [isSaving, setIsSaving] = useState(true);
  const [defaultCurrency, setDefaultCurrency] = useState("QAR");
  const [currencies, setCurrencies] = useState([]);
  const [error, setError] = useState("");

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((active) => !active), []);
  const toastMarkup = active ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleActive}
    />
  ) : null;

  useEffect(() => {
    axios
      .get(`/settings/currencies`)
      .then((result) => {
        const data = result?.data?.data;
        setDefaultCurrency(data?.currency || "QAR");
        setCurrencies(
          data?.currencies && data.currencies.length
            ? data.currencies.map((c) => ({
                code: c.code || "",
                rate: c.rate !== null && c.rate !== undefined ? String(c.rate) : "",
              }))
            : [{ code: "QAR", rate: "1" }]
        );
        setIsSaving(false);
      })
      .catch((err) => {
        console.log(err);
        setIsSaving(false);
      });
  }, []);

  const handleChangeCode = (index, value) => {
    const updated = [...currencies];
    updated[index].code = value;
    setCurrencies(updated);
    setError("");
  };

  const handleChangeRate = (index, value) => {
    const updated = [...currencies];
    updated[index].rate = value;
    setCurrencies(updated);
    setError("");
  };

  const handleAddCurrency = () => {
    const usedCodes = currencies.map((c) => c.code);
    const available = CURRENCY_OPTIONS.find((o) => !usedCodes.includes(o.value));
    setCurrencies([
      ...currencies,
      { code: available ? available.value : "", rate: "" },
    ]);
  };

  const handleRemoveCurrency = (index) => {
    setCurrencies(currencies.filter((_, i) => i !== index));
  };

  const availableForIndex = (index) => {
    const usedCodes = currencies
      .filter((_, i) => i !== index)
      .map((c) => c.code);
    return CURRENCY_OPTIONS.filter((o) => !usedCodes.includes(o.value));
  };

  function handleSave() {
    const codes = currencies.map((c) => c.code);
    const hasDuplicates = new Set(codes).size !== codes.length;
    const hasEmptyCode = currencies.some((c) => !c.code);
    const hasInvalidRate = currencies.some(
      (c) => !c.rate || Number(c.rate) <= 0
    );

    if (hasEmptyCode) {
      setError("All currencies must have a code selected");
      return;
    }
    if (hasDuplicates) {
      setError("Duplicate currency codes are not allowed");
      return;
    }
    if (hasInvalidRate) {
      setError("All rates must be greater than 0");
      return;
    }

    setError("");
    setIsSaving(true);
    axios
      .patch(`/settings/currencies`, {
        currency: defaultCurrency,
        currencies: currencies.map((c) => ({
          code: c.code,
          rate: Number(c.rate),
        })),
      })
      .then(() => {
        setIsSaving(false);
        toggleActive();
      })
      .catch((err) => {
        console.log(err);
        setError(err?.response?.data?.message || "An error occurred");
        setIsSaving(false);
      });
  }

  return (
    <Page title="Currency" narrowWidth>
      {isSaving ? <Loading /> : null}

      <Card sectioned>
        <FormLayout>
          <Select
            label="Default Currency"
            options={CURRENCY_OPTIONS}
            onChange={setDefaultCurrency}
            value={defaultCurrency}
          />

          <div style={{ marginTop: "16px" }}>
            <Text variant="headingSm" as="h6">
              Currency Rates
            </Text>
            <Text variant="bodySm" as="p" tone="subdued">
              1 {defaultCurrency} = rate
            </Text>
          </div>

          {currencies.map((curr, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: "8px",
              }}
            >
              <div style={{ flex: 1 }}>
                <Select
                  label="Code"
                  options={availableForIndex(index)}
                  onChange={(val) => handleChangeCode(index, val)}
                  value={curr.code}
                  placeholder="Select"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Rate"
                  value={curr.rate}
                  onChange={(val) => handleChangeRate(index, val)}
                  type="number"
                  step="0.0001"
                />
              </div>
              <div style={{ paddingBottom: "4px" }}>
                <Button
                  icon={DeleteIcon}
                  tone="critical"
                  onClick={() => handleRemoveCurrency(index)}
                  disabled={currencies.length <= 1}
                />
              </div>
            </div>
          ))}

          {currencies.length < 3 && (
            <Button onClick={handleAddCurrency}>Add Currency</Button>
          )}

          <InlineError message={error} />
        </FormLayout>
      </Card>
      <PageActions
        primaryAction={{
          content: "Save",
          onClick: handleSave,
          loading: isSaving && true,
        }}
      />
      {toastMarkup}
    </Page>
  );
};

export default Currency;
